import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Search,
  Funnel,
  Edit2,
  Plus,
  MoreHorizontal,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  Copy,
  CircleCheck
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import { Info } from 'lucide-react';
import Tooltip from '../addentities/Tooltip';
import apiHelper from '../../apiHelper';
import { useLoader } from '../../components/GlobalLoader';

const PAGE_SIZE = 10;

export default function Department() {
  const history = useHistory();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const filterButtonRef = useRef(null);
  const filterPopupRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState('');
  const breadcrumbs = ["Organisations", "Departments"];

  const [searchTerm, setSearchTerm] = useState('');
  const [searchStatus, setSearchStatus] = useState([]);
  const [searchParentDepartment, setSearchParentDepartment] = useState([]);
  const [searchSupervisor, setSearchSupervisor] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [suppressHighlight, setSuppressHighlight] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('entity-info');
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [supervisorSearchTerm, setSupervisorSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const filtered = useMemo(() => {
    console.log('Filtering with:', { searchTerm, searchStatus, searchParentDepartment, searchSupervisor });
    const term = searchTerm.toLowerCase();
    return departments.filter(d => {
      console.log('Department Object:', d);

      const matchesSearch =
        !searchTerm ||
        d.department_name?.toLowerCase().includes(term) ||
        d.uuid?.toLowerCase().includes(term) ||
        d.notes?.toLowerCase().includes(term);

      const matchesStatus = searchStatus.length === 0 || searchStatus.includes(d.status?.toString());

      const departmentId = d.department_id?.toString() || '';
      const matchesParentDepartment = searchParentDepartment.length === 0 ||
        searchParentDepartment.includes(departmentId);

      const matchesSupervisor = searchSupervisor.length === 0 ||
        searchSupervisor.some(sup => (d.supervisor?.toString() || '').includes(sup));

      console.log('Matches:', {
        department: d.department_name,
        matchesSearch,
        matchesStatus,
        departmentId,
        matchesParentDepartment,
        matchesSupervisor
      });
      return matchesSearch && matchesStatus && matchesParentDepartment && matchesSupervisor;
    });
  }, [searchTerm, departments, searchStatus, searchParentDepartment, searchSupervisor]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleToggleStatus = async (row) => {
    const activate = row.status_text !== 'Active';
    const action = activate ? 'activate' : 'deactivate';
    const updateData = { status: action };
    const response = await apiHelper(`departments/update-status/${row.department_id}/`, 'POST', updateData);
    if (response && response.ok) {
      setDepartments(departments.map(e =>
        e.department_id === row.department_id
          ? { ...e, status_text: activate ? 'Active' : 'Inactive' }
          : e
      ));
      setDropdownOpenId(null);
    }
  };

  const toggleSelect = id => {
    setSelectedIds(ids => {
      const newIds = ids.includes(id)
        ? ids.filter(x => x !== id)
        : [...ids, id];
      setSuppressHighlight(false);
      return newIds;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      const next =
        prev.length === pageData.length
          ? []
          : pageData.map(d => d.department_id);
      setSuppressHighlight(false);
      return next;
    });
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      showLoader();
      try {
        const response = await apiHelper('departments/list/');
        if (response && response.ok) {
          const result = await response.json();
          console.log('Fetched Departments:', result);
          setDepartments(result);
        }
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      } finally {
        setIsLoading(false);
        hideLoader();
      }
    }
    fetchData();
  }, []);

  const onRowClick = (row) => {
    setSelectedRow(row);
    setDrawerTab('entity-info');
    setDrawerOpen(true);
  };

  const removeSelectedId = (id) => {
    setSelectedIds(ids => {
      const next = ids.filter(x => x !== id);
      setSuppressHighlight(false);
      return next;
    });
  };

  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});
  const drawerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  

  const currentRow = departments.find(e => e.department_id === dropdownOpenId);

  useEffect(() => {
    function handleClickOutsideDrawer(event) {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
        setSelectedRow(null);
        setSelectedIds(ids =>
          selectedRow ? ids.filter(id => id !== selectedRow.department_id) : ids
        );
      }
    }
    document.addEventListener('mousedown', handleClickOutsideDrawer);
    return () =>
      document.removeEventListener('mousedown', handleClickOutsideDrawer);
  }, [drawerOpen, selectedRow]);

  useEffect(() => {
    function handleClickOutsideFilter(event) {
      if (
        filterPopupOpen &&
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setFilterPopupOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideFilter);
    return () => document.removeEventListener('mousedown', handleClickOutsideFilter);
  }, [filterPopupOpen]);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRefs.current[dropdownOpenId]?.contains(event.target)
      ) {
        setDropdownOpenId(null);
      }
    }

    if (dropdownOpenId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpenId]);

  const toggleDropdown = (id) => {
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
    } else {
      const btn = buttonRefs.current[id];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const dropdownWidth = 150;
        const dropdownHeight = 100;

        let left = rect.left + window.scrollX - dropdownWidth + rect.width;
        if (left < window.scrollX + 8) {
          left = window.scrollX + 8;
        }

        let top = rect.bottom + window.scrollY;
        if (top + dropdownHeight > window.scrollY + window.innerHeight) {
          top = rect.top + window.scrollY - dropdownHeight;
        }

        setDropdownPosition({ top, left });
      }
      setDropdownOpenId(id);
    }
  };

  const editDepartment = () => {
    if (selectedIds.length === 1) {
      history.push(`/department/edit-department/${selectedIds[0]}`);
    }
  };

  const filterCount = [...searchStatus, ...searchParentDepartment, ...searchSupervisor].filter(Boolean).length;

  const handleCheckboxChange = (setter, value) => (e) => {
    const isChecked = e.target.checked;
    setter(prev =>
      isChecked
        ? [...prev, value]
        : prev.filter(v => v !== value)
    );
    setCurrentPage(1);
  };

  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (filterPopupOpen && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      setFilterPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [filterPopupOpen]);

   const handleCopy = async (textToCopy) => {
        if (!textToCopy) {
            console.error('No text to copy');
            alert('Failed to copy: No text available.');
            return;
        }

        // Use Clipboard API if available and secure
        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
            } catch (err) {
                console.error('Clipboard API failed:', err);
                fallbackCopyTextToClipboard(textToCopy);
            }
        } else {
            console.warn('Clipboard API not available or not in secure context, using fallback');
            fallbackCopyTextToClipboard(textToCopy);
        }
    };

    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                console.error('Fallback copy failed');
                alert('Failed to copy text. Please copy manually.');
            }
        } catch (err) {
            console.error('Fallback copy error:', err);
            alert('Failed to copy text. Please copy manually.');
        }
    };

 useEffect(() => {
        // debug log to check what's coming in
        console.log('location.state on mount:', location.state);

        if (location && location.state && location.state.success) {
            setSuccessMessage(location.state.success);

            // clear that location.state so it doesn't reappear on refresh/back
            // for react-router v5: history.replace(pathname, state)
            history.replace(location.pathname, {});

            // auto-hide the success message after 3-4s
            const t = setTimeout(() => setSuccessMessage(''), 4000);
            return () => clearTimeout(t);
        }
    }, [location, history]);


    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 4000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

  return (
    <div className="company-body">
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="breadcrumb-item">
            {crumb}
            {index !== breadcrumbs.length - 1}
          </span>
        ))}
      </div>

       {successMessage && (
                <div
                    className="d-flex align-items-center justify-content-between"
                    role="alert"
                    style={{
                        boxShadow: "0px 4px 10px 0px #0000001F",
                        padding: "15px 10px",
                        borderRadius: "12px",
                        border: "1px solid #30B473",
                        backgroundColor: "#E6F7EF",
                        color: "#0D6438",
                        fontSize: "12px",
                        position: "fixed",
                        top: "10px",
                        width: "calc(100% - 20px)",
                        maxWidth: "640px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2000,
                    }}
                >
                    <div className="d-flex align-items-center">
                        <CircleCheck className="me-2" />
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                            }}>
                            <strong>{successMessage}</strong>
                        </div>
                    </div>
     <X
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSuccessMessage("")}
                    />
                </div>
            )}

      <div className="info-section">
        <div className="info-card">
          <div className="info-header">
            <h4 className="info-title">Departments</h4>
          </div>

          <div className="entities-tab">
            <div className="toolbar">
              <div className="toolbar-left">
                <div className="search-box">
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button
                  ref={filterButtonRef}
                  className="icon-btn new-look"
                  onClick={() => setFilterPopupOpen(!filterPopupOpen)}
                >
                  <Funnel size={18} /> Filter
                  {filterCount > 0 && (
                    <span className="filter-counts"
                      style={{
                        background: '#000',
                        color: '#ffffff',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        marginLeft: '5px'
                      }}>
                      {filterCount}
                    </span>
                  )}
                </button>
              </div>
              <div className="toolbar-right">
                <button className="icon-btn">
                  <Edit2 size={18} onClick={editDepartment} />
                </button>
                <button
                  className="icon-btn"
                  onClick={() => history.push("/department/add-department")}
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            {filterPopupOpen && !isLoading && departments.length > 0 && (
              <div
                ref={filterPopupRef}
                className="filter-popup"
                style={{
                  position: 'absolute',
                  top: filterPosition.top,
                  left: filterPosition.left,
                  background: '#fff',
                  padding: '16px',
                  borderRadius: '8px',
                  width: '300px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: '1002'
                }}
              >
                <div className="filter-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                  <button className="close-btn" onClick={() => setFilterPopupOpen(false)}>
                    <X size={20} />
                  </button>
                </div>
                <div className="filter-content" style={{ marginBottom: '16px' }}>
                  <div className="filter-section" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px' }}>Status</label>
                    <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                      {['1', '0'].map(status => (
                        <label key={status} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={searchStatus.includes(status)}
                            onChange={handleCheckboxChange(setSearchStatus, status)}
                          />
                          <span style={{ marginLeft: '8px' }}>
                            {status === '1' ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="filter-section" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px' }}>Parent Entity</label>
                    <div className="search-box-filter" style={{ position: 'relative', border: '1px solid #6c757d', borderRadius: '.5rem', padding: '4px', minHeight: '32px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                      {searchParentDepartment.map(id => {
                        const item = departments.find(d => d.department_id?.toString() === id);
                        return item && (
                          <span
                            key={id}
                            style={{
                              backgroundColor: '#FFEB00',
                              color: '#212529',
                              borderRadius: '16px',
                              padding: '4px 8px',
                              margin: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              lineHeight: 'normal',
                            }}
                          >
                            {item.department_name}
                            <button
                              onClick={() => handleCheckboxChange(setSearchParentDepartment, id)({ target: { checked: false } })}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <X size={14} color='white' />
                            </button>
                          </span>
                        );
                      })}
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '100px' }}>
                        <Search size={16} style={{ marginRight: '4px' }} />
                        <input
                          type="text"
                          placeholder="Search Parent Entity…"
                          value={parentSearchTerm}
                          onChange={e => setParentSearchTerm(e.target.value)}
                          style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                    <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                      {[...new Set(departments
                        .filter(dept => dept.department_name.toLowerCase().includes(parentSearchTerm.toLowerCase()))
                        .map(item => item.department_id?.toString()))]
                        .slice(0, 3)
                        .map((id, index) => {
                          const item = departments.find(d => d.department_id?.toString() === id);
                          return item ? (
                            <label key={index} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={searchParentDepartment.includes(id)}
                                onChange={handleCheckboxChange(setSearchParentDepartment, id)}
                              />
                              <span style={{ marginLeft: '8px' }}>{item.department_name}</span>
                            </label>
                          ) : null;
                        })}
                    </div>
                  </div>
                  <div className="filter-section">
                    <label style={{ display: 'block', marginBottom: '4px' }}>Supervisor</label>
                    <div className="search-box-filter" style={{ position: 'relative', border: '1px solid #6c757d', borderRadius: '.5rem', padding: '4px', minHeight: '32px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                      {searchSupervisor.map(supervisorValue => (
                        <span
                          key={supervisorValue}
                          style={{
                            backgroundColor: '#FFEB00',
                            color: '#212529',
                            borderRadius: '16px',
                            padding: '4px 8px',
                            margin: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            lineHeight: 'normal',
                          }}
                        >
                          {supervisorValue || 'N/A'}
                          <button
                            onClick={() => handleCheckboxChange(setSearchSupervisor, supervisorValue)({ target: { checked: false } })}
                            style={{
                              background: 'none',
                              border: 'none',
                              marginLeft: '20px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '0',
                              lineHeight: '14px',
                            }}><X size={14} strokeWidth={3} />
                          </button>
                        </span>
                      ))}
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '100px' }}>
                        <Search size={16} style={{ marginRight: '4px' }} />
                        <input
                          type="text"
                          placeholder="Search Supervisor…"
                          value={supervisorSearchTerm}
                          onChange={e => setSupervisorSearchTerm(e.target.value)}
                          style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                    <div className="filter-options" style={{ maxHeight: '90px', overflowY: 'auto' }}>
                      {[...new Set(departments
                        .filter(dept => dept.supervisor && dept.supervisor.toLowerCase().includes(supervisorSearchTerm.toLowerCase()))
                        .map(item => item.supervisor || ''))]
                        .slice(0, 3)
                        .map((supervisorValue, index) => (
                          <label key={index} className="form-check" style={{ cursor: 'pointer', fontWeight: '400' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={searchSupervisor.includes(supervisorValue)}
                              onChange={handleCheckboxChange(setSearchSupervisor, supervisorValue)}
                            />
                            <span style={{ marginLeft: '8px' }}>{supervisorValue || 'N/A'}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="filter-footer" style={{ textAlign: 'Center' }}>
                  <button
                    className="filter-btn"
                    onClick={() => setFilterPopupOpen(false)}
                    style={{
                      background: 'black',
                      color: 'white',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Filter
                  </button>
                </div>
              </div>
            )}

            <div className="table-wrapper">
              <table className="entities-table">
                <thead>
                  <tr>
                    <th>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={
                            selectedIds.length === pageData.length && pageData.length > 0
                          }
                          onChange={toggleAll}
                        />
                      </div>
                    </th>
                    <th>
                      <span>
                        Department Name <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th>
                      <span>
                        Status <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th>
                      <span>
                        ID <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th>
                      <span>
                        Parent Department <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th>
                      <span>
                        Supervisor <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.length > 0 ? (
                    pageData.map((row, idx) => {
                      const isRowSelected =
                        selectedIds.includes(row.department_id) &&
                        !suppressHighlight;

                      return (
                        <tr
                          key={row.department_id}
                          className={[
                            idx % 2 ? 'striped' : '',
                            isRowSelected ? 'row-selected' : ''
                          ].join(' ')}
                          onClick={() => onRowClick(row)}
                        >
                          <td>
                            <div className="form-check" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedIds.includes(row.department_id)}
                                onChange={e => {
                                  toggleSelect(row.department_id);
                                  if (e.target.checked) {
                                    setSelectedRow(row);
                                  } else if (selectedRow?.department_id === row.department_id) {
                                    setSelectedRow(null);
                                  }
                                }}
                              />
                            </div>
                          </td>
                          <td>{row.department_name}</td>
                          <td>
                            <span className={`status ${row.status_text.toLowerCase()}`}>
                              {row.status_text}
                            </span>
                          </td>
                          <td>{row.department_id}</td>
                          <td>{row.parent_name || 'NA'}</td>
                          <td>{row.supervisor_name || 'NA'}</td>
                          <td style={{ textAlign: "center", position: "relative" }}>
                            <button
                              ref={el => (buttonRefs.current[row.department_id] = el)}
                              className="icon-btn"
                              onClick={e => {
                                e.stopPropagation();
                                toggleDropdown(row.department_id);
                              }}
                            >
                              <MoreHorizontal size={20} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                        No departments match the selected filters.
                      </td>
                    </tr>
                  )}

                  {pageData.length < PAGE_SIZE && pageData.length > 0 && (
                    [...Array(PAGE_SIZE - pageData.length)].map((_, idx) => (
                      <tr
                        key={`empty-${idx}`}
                        className="empty-row"
                        onClick={() => { }}
                      >
                        <td colSpan={7}> </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {dropdownOpenId !== null && (
                <div
                  ref={dropdownRef}
                  className="backdrop-cls"
                  style={{
                    position: "absolute",
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    background: "white",
                    boxShadow: "0px 1px 4px 0px #0000001A",
                    zIndex: 1000,
                    minWidth: 152,
                    borderRadius: 8,
                    padding: 12,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="btn-align-forall">
                    <div className="common-for-all">
                      <button
                        onClick={() => {
                          onRowClick(currentRow);
                          setDropdownOpenId(null); // 👈 Close dropdown
                        }}
                      >
                        View Details
                      </button>
                    </div>

                    <div className="common-for-all">
                      <button
                        onClick={() => {
                          history.push(`/department/edit-department/${currentRow.department_id}`);
                          setDropdownOpenId(null); // 👈 Close dropdown
                        }}
                      >
                        Edit
                      </button>
                    </div>

                    <div className="common-for-all">
                      <button
                        onClick={() => {
                          handleToggleStatus(currentRow);
                          setDropdownOpenId(null); // 👈 Close dropdown
                        }}
                        className="deactivate-cls"
                      >
                        {currentRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </div>

            {filtered.length > 0 && (
              <div className="pagination">
                <div className="pagination-info">
                  {`Showing ${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, filtered.length)} of ${filtered.length} items`}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      className={currentPage === i + 1 ? 'active' : ''}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {drawerOpen && selectedRow && (
        <>
          <div className="drawer-overlay" />
          <div className="entity-drawer" ref={drawerRef}>
            <div className="drawer-header">
              <h3>Department Info</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setDrawerOpen(false);
                  setSelectedIds(ids => ids.filter(id => id !== selectedRow.department_id));
                  setSelectedRow(null);
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className="main-drawer-inner">
              <div className="drawer-title">
                <h2 className='pb-2'>Departments</h2>
              </div>
              <div className="drawer-tabs">
                <button
                  className={drawerTab === 'entity-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('entity-info')}
                >
                  Department Info
                </button>
                <button
                  className={drawerTab === 'additional-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('additional-info')}
                >
                  Additional Info
                </button>
              </div>
              <div className="drawer-content">
                {drawerTab === 'entity-info' ? (
                  <div className="tab-panel entity-info-panel">
                    <div className="field-block">
                      <div className="field-label">Department Name</div>
                      <div className="field-value">{selectedRow.department_name}</div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Status</div>
                      <div className="field-value">
                        <span
                          className={`status ${selectedRow.status_text.toLowerCase()}`}
                        >
                          {selectedRow.status_text}
                        </span>
                      </div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Open ID</div>
                      <div className="field-value d-flex align-items-center gap-2">
                        <span
                          style={{ color: "#2C7BE5", cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => handleCopy(selectedRow.uuid)}
                        >
                          {selectedRow.uuid}
                        </span>
                        <Copy
                          size={13}
                          onClick={() => handleCopy(selectedRow.uuid)}
                          style={{ cursor: "pointer", color: "#2C7BE5" }}
                        />
                        {copied && <span style={{ fontSize: '12px', color: "#2C7BE5" }}>Copied!</span>}
                      </div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Parent Department</div>
                      <div className="field-value">{selectedRow.parent_name || 'NA'}</div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Supervisor</div>
                      <div className="field-value">{selectedRow.supervisor_name || 'NA'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="tab-panel additional-info-panel">
                    <p className='note-type-added'>Note</p>
                    <label className='no-description-cls when-no-description-cls-adding notes-preserve'>{selectedRow.notes || 'NA'}</label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}